import {
  BOGGLE_SPECIAL_TILE_KINDS,
  BOGGLE_SPECIAL_TILE_REGISTRY,
  countBoggleSpecialTilesOnBoard,
} from '../specialTiles'
import { standardLexicon } from './lexicon'
import type { BoggleGameState, BoggleSnapshot } from './types'

export function getBoggleSnapshot(state: BoggleGameState): BoggleSnapshot {
  const intervalTimeRemainingMs =
    state.intervalDeadlineMs === null || state.isInOvertime
      ? state.intervalDeadlineMs === null
        ? null
        : 0
      : Math.max(0, state.intervalDeadlineMs - state.timeMs)
  const intervalIndex =
    state.playgroundSettings.intervalCount === null || state.intervalsRemaining === null
      ? null
      : state.playgroundSettings.intervalCount - state.intervalsRemaining + 1
  const overtimePauseRemainingMs =
    state.overtimePauseUntilMs === null
      ? 0
      : Math.max(0, state.overtimePauseUntilMs - state.timeMs)

  return {
    activeSkill: state.activeSkill,
    allowDiagonals: state.allowDiagonals,
    availableWordCount: 0,
    board: state.board.map(row => row.map(cell => cell?.letter ?? '.').join(' ')),
    cells: state.board.map(row =>
      row.map(cell =>
        cell === null
          ? null
          : {
              id: cell.id,
              letter: cell.letter,
              tags: cell.tags,
            },
      ),
    ),
    combo: state.combo,
    copiesRemaining: state.copiesRemaining,
    cols: state.cols,
    endReason: state.endReason,
    feedback: state.feedback,
    goldTilesUsed: state.goldTilesUsed,
    goldSpawnCadenceCount: state.specialTileSpawnCadenceCounts.gold,
    intervalDurationMs: state.intervalDurationMs,
    intervalIndex,
    intervalTimeRemainingMs,
    intervalsRemaining: state.intervalsRemaining,
    isInOvertime: state.isInOvertime,
    lastAction: state.lastAction,
    letterAdjacencyMode: state.letterAdjacencyMode,
    lettersRepopulate: state.lettersRepopulate,
    meter: state.meter,
    minWordLength: state.minWordLength,
    overtimeBackspaceCount: state.overtimeBackspaceCount,
    overtimePauseRemainingMs,
    overtimeRemaining: state.overtimeRemaining,
    overtimeVelocity: state.overtimeVelocity,
    pendingIntervalTimeBonusMs: state.pendingIntervalTimeBonusMs,
    phase: state.phase,
    playgroundSettings: state.playgroundSettings,
    recentWords: state.recentWords,
    runLedger: state.runLedger,
    scoredWords: state.scoredWords,
    resetsRemaining: state.resetsRemaining,
    rows: state.rows,
    rotationEnabled: state.rotationEnabled,
    samplePlayableWords: [],
    seed: state.seed,
    selectedWord: standardLexicon.getBoardWord(state.board, state.selection),
    selection: state.selection,
    selectionMode: state.selectionMode,
    shufflesRemaining: state.shufflesRemaining,
    specialTileSpawnCadenceCounts: state.specialTileSpawnCadenceCounts,
    spawnedTileCount: state.spawnedTileCount,
    skillSelection: state.skillSelection,
    swapsRemaining: state.swapsRemaining,
    timeMs: state.timeMs,
    tilesCleared: state.tilesCleared,
    totalLettersCleared: state.totalLettersCleared,
    wordsCleared: state.wordsCleared,
    zapsRemaining: state.zapsRemaining,
  }
}

export function renderBoggleGameToText(state: BoggleGameState) {
  const snapshot = getBoggleSnapshot(state)
  const intervalDurationLabel =
    snapshot.intervalDurationMs === null
      ? 'OFF'
      : `${Math.floor(snapshot.intervalDurationMs / 1000)}s`
  const intervalRemainingLabel =
    snapshot.intervalTimeRemainingMs === null
      ? 'OFF'
      : `${Math.ceil(snapshot.intervalTimeRemainingMs / 1000)}s`
  const renderedBoard = snapshot.cells.map(row =>
    row
      .map(cell => {
        if (cell === null) {
          return '.'
        }

        return (cell.tags?.length ?? 0) > 0 ? `${cell.letter}*` : cell.letter
      })
      .join(' '),
  )
  const specialTileLines = BOGGLE_SPECIAL_TILE_KINDS.map(kind => {
    const settings = snapshot.playgroundSettings.specialTiles[kind]
    const definition = BOGGLE_SPECIAL_TILE_REGISTRY[kind]
    const cadenceCount = snapshot.specialTileSpawnCadenceCounts[kind]
    const bonusLabel =
      definition.scoreBonusPerPathPosition > 0
        ? `BONUS +${definition.scoreBonusPerPathPosition} PER PATH CELL`
        : definition.timeBonusPerWordMs > 0
          ? `BONUS +${definition.timeBonusPerWordMs / 1000}s PER WORD`
          : 'BONUS SPECIAL'

    return `SPECIAL ${definition.label.toUpperCase()} ${settings.enabled ? 'ON' : 'OFF'} | START ${settings.countAtStart} | MAX ${settings.maximumCount ?? 'OFF'} | PERSIST ${settings.persistDuringBoardClears ? 'ON' : 'OFF'} | CLEAR USE ${settings.clearOnUse ? 'ON' : 'OFF'} | CLEAR DISUSE ${settings.clearOnDisuse ? 'ON' : 'OFF'} | REPLACE_CLEAR ${settings.replaceWhenCleared ? 'ON' : 'OFF'} | RESET_EMPTY ${settings.resetWhenEmpty ? 'ON' : 'OFF'} | SPAWN CHANCE ${settings.spawnChancePercent === null ? 'OFF' : `${settings.spawnChancePercent}%`} | SPAWN EACH SUBMIT ${settings.spawnXTilesOnSubmit ?? 'OFF'} | SPAWN EVERY ${settings.spawnEveryXTiles ?? 'OFF'} | CONVERT ON SUBMIT ${settings.convertXTilesOnSubmit ?? 'OFF'} | SPREAD USE ${settings.spreadToAllAdjacentOnUse ? 'ALL ADJACENT' : 'OFF'} | SPREAD DISUSE ${settings.spreadToOneAdjacentOnDisuse ? '1 ADJACENT' : 'OFF'} | CADENCE ${cadenceCount} | SPAWNED ${snapshot.spawnedTileCount} | ${bonusLabel} | LIVE ${countBoggleSpecialTilesOnBoard(state.board, kind)}`
  })
  const lines = [
    `SEED ${snapshot.seed} PHASE ${snapshot.phase.toUpperCase()} END ${snapshot.endReason ?? '-'} TILES ${snapshot.tilesCleared} GOLD ${snapshot.goldTilesUsed} WORDS ${snapshot.wordsCleared} LETTERS ${snapshot.totalLettersCleared} ZAPS ${snapshot.zapsRemaining} SWAPS ${snapshot.swapsRemaining} COPIES ${snapshot.copiesRemaining} RESETS ${snapshot.resetsRemaining} SHUFFLES ${snapshot.shufflesRemaining} TIME ${snapshot.timeMs} INTERVAL ${snapshot.intervalIndex ?? 'OFF'} OF ${snapshot.playgroundSettings.intervalCount ?? 'OFF'} DURATION ${intervalDurationLabel} REMAINING ${intervalRemainingLabel}`,
    `LAST ${snapshot.lastAction.kind.toUpperCase()} ${snapshot.lastAction.word ?? '-'} TILES ${snapshot.lastAction.tilesCleared ?? 0} GOLD ${snapshot.lastAction.goldTilesUsed ?? 0} :: ${snapshot.lastAction.message}`,
    `SELECT ${snapshot.selectedWord || '-'} | MODE ${snapshot.selectionMode ?? '-'} | ACTIVE ${snapshot.activeSkill ?? '-'} | SKILL ${snapshot.skillSelection ? `${snapshot.skillSelection.mode}@${snapshot.skillSelection.position.col},${snapshot.skillSelection.position.row}` : '-'} | OVERTIME ${snapshot.isInOvertime ? 'ON' : 'OFF'} ${snapshot.overtimeRemaining === null ? '-' : snapshot.overtimeRemaining.toFixed(3)} | PAUSE ${snapshot.overtimePauseRemainingMs} | VEL ${snapshot.overtimeVelocity.toFixed(3)} | BACKSPACE ${snapshot.overtimeBackspaceCount} | METER ${snapshot.playgroundSettings.meter.enabled ? `${snapshot.meter.value.toFixed(3)}/${snapshot.playgroundSettings.meter.capacity}${snapshot.meter.overmeterValue > 0 ? ` OVER ${snapshot.meter.overmeterValue.toFixed(3)}` : ''}${snapshot.meter.burstActive ? ' BURST' : ''}` : 'OFF'} | PENDING INTERVAL BONUS ${snapshot.pendingIntervalTimeBonusMs} | FEEDBACK ${snapshot.feedback ? `${snapshot.feedback.kind.toUpperCase()} :: ${snapshot.feedback.message}` : '-'}`,
    `SETTINGS ${snapshot.cols}x${snapshot.rows} | ADJACENCY ${snapshot.letterAdjacencyMode.toUpperCase()} | DIAGONALS ${snapshot.allowDiagonals ? 'ON' : 'OFF'} | MIN ${snapshot.minWordLength} | REFILL ${snapshot.lettersRepopulate ? 'ON' : 'OFF'} | REDRAW ${snapshot.playgroundSettings.redrawBoardAfterWord ? 'ON' : 'OFF'} | END ON SUBMIT ${snapshot.playgroundSettings.endIntervalOnSubmit ? 'ON' : 'OFF'} | ROTATION ${snapshot.rotationEnabled ? 'ON' : 'OFF'} | SCORE MODE ${snapshot.playgroundSettings.scoreMode.toUpperCase()} | SCORE CAP ${snapshot.playgroundSettings.scoreBonusMaxLength ?? 'OFF'} | EXTRA LETTER TIME ${snapshot.playgroundSettings.scoreBonusMaxLength === null || snapshot.playgroundSettings.intervalCount === null || snapshot.playgroundSettings.extraLetterBonusTime <= 0 ? 'OFF' : `+${snapshot.playgroundSettings.extraLetterBonusTime}s`}`,
    ...specialTileLines,
    `PLAYABLE ${snapshot.availableWordCount} | SAMPLE ${snapshot.samplePlayableWords.join(', ') || '-'}`,
    'GRID',
    ...renderedBoard,
  ]

  return lines.join('\n')
}
